import type { PlatformSubpageConfig } from '../types';
import { defaultCtas, localImage } from './shared';

export const deviceManagementConfig: PlatformSubpageConfig = {
  slug: 'device-management',
  path: '/platform/device-management',
  breadcrumbLabel: 'Device Management',
  hero: {
    title: 'Secure orchestration of interconnected IoT devices',
    subtitle:
      'Seamlessly connect and manage diverse devices to collect asset data with our scalable device management platform.',
    ...defaultCtas,
  },
  heroImage: {
    src: localImage('/images/home/ota_updates_android.png'),
    alt: 'OTA firmware update on mobile',
    width: 800,
    height: 520,
  },
  eduIntro: {
    title: 'What is IoT device management?',
    description:
      'IoT device management is the process of remotely overseeing the entire device lifecycle—from onboarding and monitoring to diagnosing and updating—within an IoT ecosystem. Collect large-scale real-time data from traditional and modern assets to enable advanced analytics, efficiency, security, and seamless automation.',
  },
  advantages: {
    eyebrow: 'COMPETITIVE EDGE',
    title: 'A competitive edge with future-fit device management',
    items: [
      {
        icon: 'shield',
        title: 'Intact Security',
        description:
          'Safeguard connected devices with robust encryption, TLS, and client certificates for secure edge communication.',
      },
      {
        icon: 'activity',
        title: 'Dynamically Scalable',
        description:
          'Streamline managing devices at scale by remotely adding, deleting, and configuring as your business grows.',
      },
      {
        icon: 'layers',
        title: 'Device Agnostic',
        description:
          'Eliminate compatibility testing—secure multi-vendor device and gateway support out of the box.',
      },
    ],
  },
  featureShowcase: {
    eyebrow: 'DEVICE LIFECYCLE',
    title: 'Manage IoT devices easier, faster, and better',
    subtitle:
      'Manage scattered devices and their data remotely with health monitoring, secured access, and software updates.',
    items: [
      {
        title: 'Onboard devices with ease',
        description:
          'Connect IoT devices and gateways including LoRa devices to your application and let data roll in. Add devices from the gallery or onboard custom devices. Keep devices secure while registering using TLS or client certificate mode.',
        image: {
          src: localImage('/images/home/feature-cloud.jpg'),
          alt: 'Device onboarding dashboard',
          width: 1200,
          height: 800,
        },
        cta: { label: 'Learn more about devices', to: '/contact-us' },
      },
      {
        title: 'Model once, use repeatedly',
        description:
          'Model once and reuse the same configuration across devices regardless of vendor or technology. Modify the device template and all associated devices update automatically—no manual per-device changes.',
        image: {
          src: localImage('/images/home/partner-dashboard.png'),
          alt: 'Device model configuration',
          width: 1200,
          height: 800,
        },
        cta: { label: 'Create device models', to: '/contact-us' },
      },
      {
        title: 'Write business logic on the device',
        description:
          'Configure device settings—including data points, alarms, and thresholds—from the cloud. Control deployed devices and update functionality without field visits.',
        image: {
          src: localImage('/images/home/feature-firmware.jpg'),
          alt: 'Remote device configuration',
          width: 1200,
          height: 800,
        },
        cta: { label: 'Configure your devices', to: '/contact-us' },
      },
      {
        title: 'Replace and re-register without interruption',
        description:
          'Keep data flowing continuously. Rip and replace old hardware, re-register new devices with secure provisioning, and retain existing system mappings.',
        image: {
          src: localImage('/images/home/white-label-app-image.png'),
          alt: 'Device replacement workflow',
          width: 1200,
          height: 800,
        },
        cta: { label: 'Re-register devices', to: '/contact-us' },
      },
      {
        title: 'Stay on top of device updates',
        description:
          'Deliver over-the-air updates and configurations to improve device functionality and protect against security vulnerabilities—without manual intervention.',
        image: {
          src: localImage('/images/home/ota_updates_android.png'),
          alt: 'OTA software updates',
          width: 1200,
          height: 800,
        },
        cta: { label: 'Explore OTA updates', to: '/contact-us' },
      },
    ],
  },
  capabilities: {
    eyebrow: 'CAPABILITIES',
    title: 'OTA, provisioning, and diagnostics',
    subtitle: 'Core fleet operations built for production scale.',
    items: [
      { icon: 'refresh', title: 'Staged OTA Rollouts', description: 'Wave-based updates with automatic rollback.' },
      { icon: 'wifi', title: 'SmartConfig & AP Provisioning', description: 'Multiple onboarding paths for any product.' },
      { icon: 'zap', title: 'Remote Diagnostics', description: 'Reboot, reset, and log pull from the dashboard.' },
      { icon: 'activity', title: 'Fleet Health Monitoring', description: 'Version, connectivity, and error rate views.' },
      { icon: 'layers', title: 'Version Control', description: 'Channels and targeting by group or region.' },
      { icon: 'chart', title: 'Diagnostics Dashboards', description: 'Support teams see issues before users do.' },
    ],
  },
  ctaBanner: {
    title: 'Manage all your devices efficiently now!',
    ctaLabel: 'Get Started',
    ctaTo: '/contact-us',
    imageSrc: '/images/cta/solution-cta-robot.webp',
    imageAlt: 'Industrial automation',
  },
  faqs: {
    title: 'Frequently asked questions',
    items: [
      {
        question: 'Why is device management important in IoT?',
        answer:
          'IoT device management enables your organization to efficiently monitor and manage connected devices—including provisioning, securing against threats, remote operations, and centralized control.',
      },
      {
        question: 'What security measures are necessary for IoT device management?',
        answer:
          'Crucial measures include device authentication, encryption in transit, secure boot, access controls, role-based permissions, and regular security updates.',
      },
      {
        question: 'How do you ensure secure onboarding and provisioning?',
        answer:
          'We implement robust onboarding through unique device credentials, industry-standard protocols, and mutual authentication methods.',
      },
      {
        question: 'How are security incidents or anomalies detected?',
        answer:
          'Continuous monitoring with anomaly detection sends automated alerts for investigation and remediation when incidents occur.',
      },
    ],
  },
  sections: {
    eyebrow: 'ARCHITECTURE',
    title: 'Provision, update, and monitor workflows',
    subtitle: 'Walk through the device lifecycle from onboarding to operations.',
  },
};

export const deviceMgmtSteps = [
  {
    id: 'provision',
    title: 'Provision',
    description: 'Onboard devices via SmartConfig, AP mode, or factory credentials.',
    detail: 'Bulk import MAC addresses and assign firmware channels before first connect.',
  },
  {
    id: 'ota',
    title: 'OTA Update',
    description: 'Roll out firmware in staged waves with automatic rollback on failure.',
    detail: 'Target by device group, region, or firmware version with real-time progress.',
  },
  {
    id: 'monitor',
    title: 'Monitor',
    description: 'Track fleet health, logs, and remote diagnostics from the dashboard.',
    detail: 'Trigger reboot, log pull, or config push without physical access.',
  },
] as const;
